import { Navigate } from "react-router-dom";
import { isLive } from "utils/environment";

/**
 * Route guard for features that are hidden on live (shown only on local + dev).
 *
 * Hiding the nav entry isn't enough — a user can still type the URL. Wrapping a
 * route's `lazy` loader with `blockOnLive` makes the route redirect to /overview
 * on live, while behaving normally everywhere else.
 *
 * Usage (wraps an existing React Router `lazy` loader, preserving its return
 * value — Component, ErrorBoundary, etc. — on non-live):
 *
 * // {
 * //   path: "mybriefcase",
 * //   lazy: blockOnLive(async () => {
 * //     const mod = await import("components/UI/BriefcaseLayout");
 * //     return { Component: mod.default };
 * //   }),
 * //   children: [ ... ],
 * // }
 *
 * When applied to a layout/parent route, the redirect replaces the parent's
 * Outlet on live, so the whole subtree is blocked.
 */
export const blockOnLive =
  <T,>(loader: () => Promise<T>) =>
  async (): Promise<T | { Component: () => JSX.Element }> => {
    if (isLive()) {
      return { Component: () => <Navigate to="/overview" replace /> };
    }
    return loader();
  };
